import { extends as _extends } from './_virtual/_rollupPluginBabelHelpers.js';
import React, { createContext, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { noop, callAccounts, productToGAProduct, quoteItemToGAProduct, next, onlyKeys } from './helpers.js';

var GA_IMPRESSION_KEYS = ["item_id", "item_name", "item_list_name", "item_brand", "item_category", "item_category2", "item_category3", "item_category4", "item_category5", "price", "index"];
var impressionIdCounter = 0;
/**
 * Generates a new unique id (unique over the runtime of the app).
 */

var getNextImpressionId = function getNextImpressionId() {
  return impressionIdCounter++;
};
var AnalyticsContext = createContext({
  g: noop,
  register: function register() {
    return 0;
  },
  unregister: noop,
  registerProductClick: noop,
  registerProductDetailsView: noop,
  registerContentClick: noop,
  registerModifyCart: noop,
  registerAddToCart: noop,
  registerRemoveFromCart: noop,
  registerBeginCheckoutProcess: noop,
  registerCheckoutProgress: noop,
  registerCheckoutStep: noop,
  registerCheckoutSuccess: noop,
  grantConsentNecessary: noop,
  grantConsentStatistical: noop,
  grantConsentMarketing: noop,
  denyConsentStatistical: noop,
  denyConsentMarketing: noop,
  searchTerm: noop,
  viewedCart: noop,
  loggedIn: noop
});

var PageView = function PageView(_ref) {
  var accounts = _ref.accounts,
      g = _ref.g;

  var _useLocation = useLocation(),
      pathname = _useLocation.pathname,
      search = _useLocation.search,
      hash = _useLocation.hash;
  /**
   * Set a timeout for 1 second to allow helmet to
   * set the correct page title. See link for info:
   * https://github.com/nfl/react-helmet/issues/189
   */


  useEffect(function () {
    setTimeout(function () {
      accounts.forEach(function (account) {
        g("event", "page_view", {
          page_path: pathname + (search || "") + (hash || ""),
          // eslint-disable-line camelcase
          page_title: window.document.title,
          // eslint-disable-line camelcase
          page_location: window.location.href,
          // eslint-disable-line camelcase
          send_to: account // eslint-disable-line camelcase

        });
      });
    }, 1000);
  }, [pathname]);
  return null;
};
/**
 * Component adding a google analytics callback to the context.
 *
 * Renders a single wrapped child.
 */


var WithAnalytics = function WithAnalytics(props) {
  var children = props.children,
      accounts = props.accounts,
      currencyCode = props.currencyCode,
      nonce = props.nonce,
      _props$isB2b = props.isB2b,
      isB2b = _props$isB2b === void 0 ? false : _props$isB2b;
  var timeout = props.timeout === undefined ? 50 : props.timeout;
  var margin = props.margin === undefined ? -50 : props.margin;
  var throttle = useRef(null);
  var elements = useRef([]);
  var g = callAccounts(accounts);
  /**
   * Calculates the visible product cards used to send product impressions.
   */

  var calculateVisible = function calculateVisible() {
    throttle.current = null;
    var visible = [];
    var items = [];
    var windowHeight = window.innerHeight || document.documentElement && document.documentElement.clientHeight;
    var windowWidth = window.innerWidth || document.documentElement && document.documentElement.clientWidth;

    for (var i = 0; i < elements.current.length; i++) {
      if (!elements.current[i]) {
        continue;
      }

      var rect = elements.current[i].el.getBoundingClientRect();

      if (rect.top <= windowHeight + margin && rect.top + rect.height >= margin && rect.left <= windowWidth + margin && rect.left + rect.width >= margin) {
        visible.push(i);
      }
    }

    for (var _i = 0, _visible = visible; _i < _visible.length; _i++) {
      var element = _visible[_i];
      items.push(onlyKeys(elements.current[element].data, GA_IMPRESSION_KEYS));
      delete elements.current[element];
    }

    if (items.length > 0) {
      g("event", "view_item_list", {
        items: items
      });
      items = [];
    }
  };

  var registerProductClick = function registerProductClick(product, list, position) {
    g("event", "select_item", {
      item_list_name: list,
      // eslint-disable-line camelcase
      items: [productToGAProduct(product, isB2b, list, position)]
    });
  };

  var registerProductDetailsView = function registerProductDetailsView(product, value, list, position) {
    g("event", "view_item", {
      currency: currencyCode,
      value: value,
      items: [productToGAProduct(product, isB2b, list, position)]
    });
  };

  var registerContentClick = function registerContentClick(content, contentId) {
    g("event", "select_content", {
      content_type: content,
      // eslint-disable-line camelcase
      content_id: contentId // eslint-disable-line camelcase

    });
  };

  var registerModifyCart = function registerModifyCart(product, type, value) {
    g("event", type, {
      currency: currencyCode,
      value: value,
      items: [productToGAProduct(product, isB2b)]
    });
  };

  var registerBeginCheckoutProcess = function registerBeginCheckoutProcess(products, value, coupon) {
    var items = products.map(function (product) {
      return quoteItemToGAProduct(product, isB2b, coupon);
    });
    g("event", "begin_checkout", {
      currency: currencyCode,
      value: value,
      items: items,
      coupon: coupon
    });
  };

  var registerCheckoutProgress = function registerCheckoutProgress(products, coupon) {
    var items = products.map(function (product) {
      return quoteItemToGAProduct(product, isB2b);
    });
    g("event", "checkout_progress", {
      items: items,
      coupon: coupon
    });
  };

  var registerCheckoutStep = function registerCheckoutStep(step, option, value) {
    g("event", "set_checkout_option", {
      step: step,
      option: option,
      value: value
    });
  };

  var registerCheckoutSuccess = function registerCheckoutSuccess(order, totalPoints, coupon) {
    var _order$shipping, _order$shipping2, _order$grandTotal, _order$grandTotal2, _order$grandTotal3;

    var items = order.items.map(function (item) {
      var product = item.configOption ? item.configOption.product : item.product;
      return productToGAProduct(_extends({}, product, {
        price: item.rowTotal,
        qty: item.qty
      }), isB2b);
    });
    var shipping = isB2b ? (_order$shipping = order.shipping) == null ? void 0 : _order$shipping.total.exVat : (_order$shipping2 = order.shipping) == null ? void 0 : _order$shipping2.total.incVat;
    var value = isB2b ? (_order$grandTotal = order.grandTotal) == null ? void 0 : _order$grandTotal.exVat : (_order$grandTotal2 = order.grandTotal) == null ? void 0 : _order$grandTotal2.incVat;
    g("event", "purchase", {
      transaction_id: order.id,
      // eslint-disable-line camelcase
      value: value,
      currency: currencyCode,
      tax: (_order$grandTotal3 = order.grandTotal) == null ? void 0 : _order$grandTotal3.vat,
      shipping: shipping,
      coupon: coupon,
      total_points: totalPoints,
      // eslint-disable-line camelcase
      items: items
    });
  };

  var onScroll = function onScroll() {
    if (!throttle.current) {
      throttle.current = next(calculateVisible, timeout);
    }
  };

  var register = function register(el, data) {
    var id = getNextImpressionId();
    elements.current[id] = {
      el: el,
      data: data
    };
    onScroll();
    return id;
  };
  /**
   * Unregisters the impression-element associated with the id
   * obtained from `register`.
   */


  var unregister = function unregister(id) {
    delete elements.current[id];
  };

  var grantConsentNecessary = function grantConsentNecessary() {
    g("consent", "update", {
      functionality_storage: "granted",
      // eslint-disable-line camelcase
      personalization_storage: "granted",
      // eslint-disable-line camelcase
      security_storage: "granted" // eslint-disable-line camelcase

    });
  };

  var grantConsentStatistical = function grantConsentStatistical() {
    g("consent", "update", {
      analytics_storage: "granted" // eslint-disable-line camelcase

    });
  };

  var denyConsentStatistical = function denyConsentStatistical() {
    g("consent", "update", {
      analytics_storage: "denied" // eslint-disable-line camelcase

    });
  };

  var grantConsentMarketing = function grantConsentMarketing() {
    g("consent", "update", {
      ad_storage: "granted",
      // eslint-disable-line camelcase
      ad_user_data: "granted",
      // eslint-disable-line camelcase
      ad_personalization: "granted" // eslint-disable-line camelcase

    });
  };

  var denyConsentMarketing = function denyConsentMarketing() {
    g("consent", "update", {
      ad_storage: "denied",
      // eslint-disable-line camelcase
      ad_user_data: "denied",
      // eslint-disable-line camelcase
      ad_personalization: "denied" // eslint-disable-line camelcase

    });
  };

  var searchTerm = function searchTerm(searchString) {
    g("event", "search", {
      search_term: searchString // eslint-disable-line camelcase

    });
  };

  var viewedCart = function viewedCart(value, products) {
    var items = products.map(function (product) {
      return productToGAProduct(product, isB2b);
    });
    g("event", "view_cart", {
      currency: currencyCode,
      value: value,
      items: items
    });
  };

  var loggedIn = function loggedIn() {
    g("event", "login", {
      method: "Login"
    });
  }; // TODO: Proper return type


  useEffect(function () {
    onScroll();
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    window.addEventListener("resize", onScroll, {
      passive: true
    });
    return function () {
      window.removeEventListener("scroll", onScroll, {
        passive: true
      });
      window.removeEventListener("resize", onScroll, {
        passive: true
      });
    };
  }, []);

  if (process.env.NODE_ENV !== "production" && children === null) {
    console.error("<AnalyticsProvider /> need at least one child");
  }

  var opts = {};

  if (nonce) {
    opts.nonce = nonce;
  }

  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Helmet, null, accounts.length === 0 && /*#__PURE__*/React.createElement("script", _extends({
    async: true
  }, opts, {
    src: "https://www.googletagmanager.com/gtag/js"
  })), accounts.map(function (account) {
    return /*#__PURE__*/React.createElement("script", _extends({
      key: account,
      async: true
    }, opts, {
      src: "https://www.googletagmanager.com/gtag/js?id=" + account
    }));
  }), /*#__PURE__*/React.createElement("script", opts, "\n            window.dataLayer = window.dataLayer || [];\n            function gtag(){dataLayer.push(arguments);}\n            gtag('consent', 'default', {\n              'ad_storage': 'denied',\n              'ad_user_data': 'denied',\n              'ad_personalization': 'denied',\n              'analytics_storage': 'denied',\n              'functionality_storage': 'denied',\n              'personalization_storage': 'denied',\n              'security_storage': 'denied',\n            });\n            gtag('js', new Date());\n            gtag('set', {\n              anonymize_ip: true,\n              currency: '" + currencyCode + "',\n            });\n\n            " + accounts.map(function (acc) {
    return "gtag('config', '" + acc + "', { send_page_view: false, custom_map: { metric5: 'total_points' } });";
  }).join("") + "\n          ")), /*#__PURE__*/React.createElement(AnalyticsContext.Provider, {
    value: {
      g: g,
      register: register,
      unregister: unregister,
      registerProductClick: registerProductClick,
      registerProductDetailsView: registerProductDetailsView,
      registerContentClick: registerContentClick,
      registerModifyCart: registerModifyCart,
      registerBeginCheckoutProcess: registerBeginCheckoutProcess,
      registerCheckoutProgress: registerCheckoutProgress,
      registerCheckoutStep: registerCheckoutStep,
      registerCheckoutSuccess: registerCheckoutSuccess,
      grantConsentNecessary: grantConsentNecessary,
      grantConsentStatistical: grantConsentStatistical,
      grantConsentMarketing: grantConsentMarketing,
      denyConsentStatistical: denyConsentStatistical,
      denyConsentMarketing: denyConsentMarketing,
      searchTerm: searchTerm,
      viewedCart: viewedCart,
      loggedIn: loggedIn
    }
  }, /*#__PURE__*/React.createElement(PageView, {
    accounts: accounts,
    g: g
  }), children));
};

export { AnalyticsContext, WithAnalytics, getNextImpressionId };
//# sourceMappingURL=context.js.map
